import React, { useEffect, useMemo, useState } from 'react';
import { standardAreaApi } from '../api/StandardAreaApi';
import { DEFAULT_FORM, SaveStandardAreaRequest, StandardAreaDto, StandardAreaForm, SupplierItemDto } from '../types/standardArea.types';
import '../styles/StandardArea.css';

interface StandardAreaModalProps {
    isOpen: boolean;
    editTarget: StandardAreaDto | null;
    onSave: (req: SaveStandardAreaRequest) => Promise<void>;
    onClose: () => void;
}

const StandardAreaModal: React.FC<StandardAreaModalProps> = ({ isOpen, editTarget, onSave, onClose }) => {
    const [form, setForm] = useState<StandardAreaForm>({ ...DEFAULT_FORM });
    const [supplierList, setSupplierList] = useState<SupplierItemDto[]>([]);
    const [search, setSearch] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!isOpen) return;
        standardAreaApi.getSupplierList().then(setSupplierList).catch(() => setSupplierList([]));
        if (editTarget) {
            setForm({
                seqNo: editTarget.seqNo,
                areaName: editTarget.areaName,
                selectedSuppIds: editTarget.suppliers.map((s) => s.suppId),
            });
        } else {
            setForm({ ...DEFAULT_FORM });
        }
        setSearch('');
    }, [isOpen, editTarget]);

    const filteredSuppliers = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return supplierList;
        return supplierList.filter((s) => (s.suppName ?? '').toLowerCase().includes(q));
    }, [supplierList, search]);

    const toggleSupplier = (suppId: string) => {
        setForm((prev) => ({
            ...prev,
            selectedSuppIds: prev.selectedSuppIds.includes(suppId)
                ? prev.selectedSuppIds.filter((id) => id !== suppId)
                : [...prev.selectedSuppIds, suppId],
        }));
    };

    const handleSave = async () => {
        if (!form.areaName.trim()) return;
        setSaving(true);
        try {
            await onSave({
                seqNo: form.seqNo,
                areaName: form.areaName.trim(),
                suppIds: form.selectedSuppIds,
                userId: '',
            });
        } finally {
            setSaving(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="sa-modal-overlay" onClick={onClose}>
            <div className="sa-modal" onClick={(e) => e.stopPropagation()}>
                <div className="sa-modal-header">
                    <h2 className="sa-modal-title">
                        {editTarget ? '표준권역 수정' : '표준권역 추가'}
                    </h2>
                </div>

                <div className="sa-modal-body">
                    {/* 권역명 */}
                    <div className="sa-form-group">
                        <label className="sa-form-label required">표준권역명</label>
                        <input
                            className="sa-form-input"
                            type="text"
                            placeholder="예: 경기파주A"
                            value={form.areaName}
                            onChange={(e) => setForm((p) => ({ ...p, areaName: e.target.value }))}
                        />
                    </div>

                    {/* 협력사 선택 */}
                    <div className="sa-supplier-section">
                        <div className="sa-supplier-section-label">
                            <span>소속 협력사</span>
                            <span className="sa-supplier-count">
                                {form.selectedSuppIds.length}개 선택됨
                            </span>
                        </div>
                        <input
                            className="sa-supplier-search"
                            type="text"
                            placeholder="협력사명 검색"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                        <div className="sa-supplier-checkbox-list">
                            {filteredSuppliers.length === 0 ? (
                                <div className="sa-supplier-empty">
                                    {search ? '검색 결과가 없습니다.' : '협력사가 없습니다.'}
                                </div>
                            ) : (
                                filteredSuppliers.map((s) => (
                                    <label
                                        key={s.suppId}
                                        className="sa-supplier-checkbox-item"
                                    >
                                        <input
                                            type="checkbox"
                                            checked={form.selectedSuppIds.includes(s.suppId)}
                                            onChange={() => toggleSupplier(s.suppId)}
                                        />
                                        {s.suppName || s.suppId}
                                    </label>
                                ))
                            )}
                        </div>
                    </div>
                </div>

                <div className="sa-modal-footer">
                    <button className="btn-modal-cancel" onClick={onClose}>
                        취소
                    </button>
                    <button
                        className="btn-modal-save"
                        onClick={handleSave}
                        disabled={saving || !form.areaName.trim()}
                    >
                        {saving ? '저장 중...' : '저장'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default StandardAreaModal;
